import { Drawer, Input, Select, message, Upload, DatePicker } from "antd";
import { UploadOutlined } from '@ant-design/icons';
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../core/data/redux/store";
import { createPfWithdrawal, fetchAllPfWithdrawals, updatePfWithdrawal } from "../../../core/data/redux/payroll/Accounting/providentFund/pfWithdrawal/pfWithdrawalActions";
import { status } from "../../../core/common/selectoption/selectoption";
import { fetchAllPfEmployees } from "../../../core/data/redux/payroll/Accounting/providentFund/pfEmployee/pfEmployeeActions";
import { getEmployeeByUserId } from "../../../core/data/redux/employee/employeeAction";
import dayjs, { Dayjs } from "dayjs";

const withdrawalReasonOptions = [
    { value: "medical_emergency", label: "Medical Emergency" },
    { value: "education", label: "Education" },
    { value: "marriage", label: "Marriage" },
    { value: "home_purchase", label: "Home Purchase" },
    { value: "retirement", label: "Retirement" },
    { value: "unemployment", label: "Unemployment" },
    { value: "other", label: "Other" },
];

const requestStatusOptions = [
    { value: "Pending", label: "Pending" },
    { value: "Approved", label: "Approved" },
    { value: "Rejected", label: "Rejected" },
    { value: "Paid", label: "Paid" },
];

const PfWithdrawalRequestModal: React.FC<any> = ({
    modalType,
    handleCancel,
    isModalVisible,
    selectedRowData,
    setTableKey,
}) => {
    const dispatch = useDispatch<AppDispatch>();
    const { user } = useSelector((state: any) => state.auth);
    const { employeesbyUser } = useSelector((state: any) => state.employee);
    const { loadingStates } = useSelector(
        (state: any) => state.providentFund.pfWithdrawl
    );
    const { records: pfEmployees } = useSelector(
        (state: any) => state.providentFund.pfEmployeeMapping
    );

    const isLoading = modalType === "edit" ? loadingStates.update : loadingStates.create;

    const [formData, setFormData] = useState<{
        pfEmployeeId: number | undefined;
        requestDate: Dayjs | null;
        amountRequested: string;
        withdrawalReason: string | undefined;
        requeststatus: string;
        status: string;
        createdBy: number;
    }>({
        pfEmployeeId: undefined,
        requestDate: null,
        amountRequested: "",
        withdrawalReason: undefined,
        requeststatus: "Pending",
        status: "active",
        createdBy: user?.UserId || 1,
    });

    const [errors, setErrors] = useState<Record<string, string>>({});
    const [fileList, setFileList] = useState<any[]>([]);

    useEffect(() => {
        if (!pfEmployees.length) dispatch(fetchAllPfEmployees());

        if (!employeesbyUser?.employeeId && user?.UserId) {
            dispatch(getEmployeeByUserId({ userId: user.UserId }));
        }
    }, [dispatch, pfEmployees.length, employeesbyUser?.employeeId, user?.UserId]);

    const resetForm = useCallback(() => {
        setFormData({
            pfEmployeeId: undefined,
            requestDate: null,
            amountRequested: "",
            withdrawalReason: undefined,
            requeststatus: "Pending",
            status: "active",
            createdBy: user?.UserId || 1,
        });
        setErrors({});
        setFileList([]);
    }, [user?.UserId]);

    useEffect(() => {
        if (modalType === "edit" && selectedRowData) {
            setFormData({
                pfEmployeeId: selectedRowData.pfEmployeeId || undefined,
                requestDate: selectedRowData.requestDate ? dayjs(selectedRowData.requestDate) : null,
                amountRequested: selectedRowData.amountRequested || "",
                withdrawalReason: selectedRowData.withdrawalReason || undefined,
                requeststatus: selectedRowData.requeststatus || "Pending",
                status: selectedRowData.status || "active",
                createdBy: selectedRowData.createdBy || user?.UserId || 1,
            });

            if (selectedRowData.attachmentFilePath) {
                setFileList([
                    {
                        uid: "-1",
                        name: selectedRowData.attachmentFilePath.split("/").pop() || "attachment",
                        status: "done",
                        url: selectedRowData.attachmentFilePath,
                    },
                ]);
            }
            setErrors({});
        } else {
            resetForm();
        }
    }, [modalType, selectedRowData, resetForm, user?.UserId]);

    const handleChange = (field: string, value: any) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        setErrors((prev) => ({ ...prev, [field]: "" }));
    };

    const handleFileUpload = {
        beforeUpload: (file: File) => {
            const isValidType =
                file.type === "application/pdf" ||
                file.type === "image/jpeg" ||
                file.type === "image/png" ||
                file.type === "image/jpg" ||
                file.type.startsWith("application/vnd.openxmlformats") ||
                file.type.startsWith("application/msword");

            if (!isValidType) {
                message.error("Only PDF, DOC, DOCX, JPG, JPEG, PNG files are allowed.");
                return false;
            }

            const isLt10M = file.size / 1024 / 1024 < 10;
            if (!isLt10M) {
                message.error("File must be smaller than 10MB!");
                return false;
            }

            return false; // manual upload
        },
        onChange: (info: any) => {
            const file = info.fileList[0]?.originFileObj;
            setFileList(info.fileList);

            setErrors((prev) => ({
                ...prev,
                attachment: file ? "" : "Attachment is required.",
            }));
        },
        fileList,
        maxCount: 1,
    };

    const validateForm = () => {
        const newErrors: Record<string, string> = {};
        let valid = true;

        if (!formData.pfEmployeeId) {
            newErrors.pfEmployeeId = "PF Employee is required.";
            valid = false;
        }
        if (!formData.requestDate) {
            newErrors.requestDate = "Request Date is required.";
            valid = false;
        }
        if (!formData.amountRequested || parseFloat(formData.amountRequested) <= 0) {
            newErrors.amountRequested = "Valid amount is required.";
            valid = false;
        }
        if (!formData.withdrawalReason) {
            newErrors.withdrawalReason = "Withdrawal Reason is required.";
            valid = false;
        }
        setErrors(newErrors);
        return valid;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;

        const file = fileList[0]?.originFileObj;

        try {
            const submitData = {
                ...formData,
                requestDate: formData.requestDate!.format("YYYY-MM-DD"),
                amountRequested: parseFloat(formData.amountRequested),
                attachment: file,
            };

            if (modalType === "edit") {
                await dispatch(
                    updatePfWithdrawal({
                        id: selectedRowData.pfWithdrawalId,
                        data: submitData,
                    })
                ).unwrap();
                message.success("PF Withdrawal Request updated successfully");
            } else {
                await dispatch(createPfWithdrawal(submitData)).unwrap();
                message.success("PF Withdrawal Request created successfully");
            }

            await dispatch(fetchAllPfWithdrawals());
            setTableKey((prev: number) => prev + 1);
            handleCancel();
            resetForm();
        } catch (error) {
            message.error("Something went wrong. Please try again.");
        }
    };

    const filteredPfEmployees = employeesbyUser?.employeeId
        ? pfEmployees.filter((pfEmp: any) => pfEmp.EmployeeID === employeesbyUser.employeeId)
        : pfEmployees;

    return (
        <Drawer
            title={modalType === "edit" ? "Edit PF Withdrawal Request" : "Add PF Withdrawal Request"}
            placement="right"
            width={500}
            onClose={() => {
                handleCancel();
                resetForm();
            }}
            visible={isModalVisible}
        >
            <form>
                {/* PF Employee */}
                <div className="mb-3">
                    <label className="form-label">PF Employee</label>
                    <Select
                        className="w-100"
                        value={formData.pfEmployeeId}
                        onChange={(value) => handleChange("pfEmployeeId", value)}
                        placeholder="Select PF Employee"
                    >
                        {filteredPfEmployees.map((pfEmployee: any) => (
                            <Select.Option key={pfEmployee.pfEmployeeId} value={pfEmployee.pfEmployeeId}>
                                {pfEmployee?.employeeName || `Employee ID: ${pfEmployee.EmployeeID}`}
                            </Select.Option>
                        ))}
                    </Select>
                    {errors.pfEmployeeId && <div className="text-danger mt-1">{errors.pfEmployeeId}</div>}
                </div>

                {/* Request Date */}
                <div className="mb-3">
                    <label className="form-label">Request Date</label>
                    <div className="input-icon-end position-relative">
                        <DatePicker
                            className="w-100"
                            value={formData.requestDate}
                            onChange={(date) => handleChange("requestDate", date)}
                            format="YYYY-MM-DD"
                            placeholder="Select request date"
                        />

                        <span className="input-icon-addon">
                            <i className="ti ti-calendar text-gray-7" />
                        </span>
                    </div>
                    {errors.requestDate && <div className="text-danger mt-1">{errors.requestDate}</div>}
                </div>

                {/* Amount Requested */}
                <div className="mb-3">
                    <label className="form-label">Amount Requested</label>
                    <Input
                        type="text"
                        value={formData.amountRequested}
                        onChange={(e) => {
                            const input = e.target.value;
                            const sanitized = input.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
                            handleChange("amountRequested", sanitized);
                        }}
                        placeholder="Enter amount"
                    />
                    {errors.amountRequested && (
                        <div className="text-danger mt-1">{errors.amountRequested}</div>
                    )}
                </div>

                {/* Withdrawal Reason */}
                <div className="mb-3">
                    <label className="form-label">Withdrawal Reason</label>
                    <Select
                        className="w-100"
                        value={formData.withdrawalReason}
                        onChange={(value) => handleChange("withdrawalReason", value)}
                        placeholder="Select withdrawal reason"
                    >
                        {withdrawalReasonOptions.map((option) => (
                            <Select.Option key={option.value} value={option.value}>
                                {option.label}
                            </Select.Option>
                        ))}
                    </Select>
                    {errors.withdrawalReason && <div className="text-danger mt-1">{errors.withdrawalReason}</div>}
                </div>


                {/* Request Status */}
                <div className="mb-3">
                    <label className="form-label">Request Status</label>
                    <Select
                        className="w-100"
                        value={formData.requeststatus}
                        onChange={(value) => handleChange("requeststatus", value)}
                        disabled={modalType === "create"}
                    >
                        {(modalType === "edit" ? requestStatusOptions : requestStatusOptions.filter(opt => opt.value === "Pending")).map(
                            (option) => (
                                <Select.Option key={option.value} value={option.value}>
                                    {option.label}
                                </Select.Option>
                            )
                        )}
                    </Select>
                </div>

                {/* Status */}
                <div className="mb-3">
                    <label className="form-label">Status</label>
                    <Select
                        className="w-100"
                        value={formData.status}
                        onChange={(value) => handleChange("status", value)}
                    >
                        {status.map((option) => (
                            <Select.Option key={option.value} value={option.value}>
                                {option.label}
                            </Select.Option>
                        ))}
                    </Select>
                </div>

                {/* Attachment Upload */}
                <div className="mb-3">
                    <Upload {...handleFileUpload}>
                        <button type="button" className="btn btn-primary">
                            <UploadOutlined /> Upload File
                        </button>
                    </Upload>
                    <small className="text-muted d-block mt-1">
                        Supported: PDF, DOC, DOCX, JPG, JPEG, PNG (Max: 10MB)
                    </small>
                </div>


                {/* Footer */}
                <div className="modal-footer mt-4">
                    <button type="button" className="btn btn-secondary me-2" onClick={() => {
                        handleCancel();
                        resetForm();
                    }}>
                        Cancel
                    </button>
                    <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={isLoading}>
                        {isLoading && <span className="spinner-border spinner-border-sm me-2" />}
                        {modalType === "edit" ? "Update Withdrawal Request" : "Add Withdrawal Request"}
                    </button>
                </div>
            </form>
        </Drawer>
    );
};

export default PfWithdrawalRequestModal;
